import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface SimplePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  ariaLabel?: string;
  // Issue #768: Optional page size selector
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  totalCount?: number;
}

/**
 * SimplePagination - A lightweight pagination component for prev/next navigation
 *
 * This component provides pagination controls with optional page size selection.
 * Issue #768: Enhanced with page size selector for better UX.
 */
export default function SimplePagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  ariaLabel = 'Pagination',
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  onPageSizeChange,
  totalCount
}: SimplePaginationProps) {
  // Don't show pagination if:
  // 1. Only 1 page AND no page size selector
  // 2. Total count is less than minimum page size (no need for pagination or selector)
  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }

  // Don't show if total count is less than minimum page size
  const minPageSize = pageSizeOptions[0] || 10;
  if (totalCount !== undefined && totalCount < minPageSize && totalPages <= 1) {
    return null;
  }

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <nav
      className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t"
      aria-label={ariaLabel}
    >
      {/* Page size selector (Issue #768 code review - fix ARIA) */}
      {onPageSizeChange && pageSize && (
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="page-size-select" className="text-gray-600">
            Items per page:
          </label>
          <select
            id="page-size-select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={disabled}
            className="px-2 py-1 border border-gray-300 rounded-md focus:ring-dusty-teal focus:border-dusty-teal disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Navigation controls */}
      <div className="flex items-center gap-4">
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1 || disabled}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label={currentPage === 1 ? "Previous page (currently on first page)" : "Go to previous page"}
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </button>
        <span className="text-sm text-gray-600" aria-live="polite" aria-atomic="true">
          Page {currentPage} of {totalPages}
          {totalCount !== undefined && ` (${totalCount} total)`}
        </span>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages || disabled}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label={currentPage === totalPages ? "Next page (currently on last page)" : "Go to next page"}
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </nav>
  );
}
